// Page « Onboarding » de l'espace Statistiques : fonctions pures, sans I/O ni React, testées par
// onboarding.test.ts (`pnpm --filter @recubebe/web test`). Seuls des TYPES viennent de '@/' : le lanceur de
// tests de Node ne résout pas l'alias.
import type {
  OnboardingAccessRow,
  OnboardingConversionRow,
  OnboardingFunnelResponse,
  OnboardingMetaResponse,
  OnboardingTutorialResponse,
} from '@/services/api.service';

/** Version du satellite qui sert l'onboarding (message « satellite à mettre à jour »). */
export const ONBOARDING_SATELLITE_VERSION = '1.0.35';

/** Capacité annoncée par le satellite écrivain (GET /api/_meta, recopiée dans project-groups). */
export const ONBOARDING_CAPABILITY = 'onboarding';

// ==================== Dates (jours de Paris, yyyy-MM-dd) ====================

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (n: number) => String(n).padStart(2, '0');

/** Vrai pour une vraie date yyyy-MM-dd (pas de 31 avril). */
export function isIsoDate(value: string | null | undefined): value is string {
  if (!value) return false;
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

function toUtc(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function fromUtc(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** `date` décalée de `days` jours. */
export function addDays(date: string, days: number): string {
  const d = toUtc(date);
  d.setUTCDate(d.getUTCDate() + days);
  return fromUtc(d);
}

/** Même jour un an avant ; le 29 février devient le 28. */
export function minusOneYear(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const year = y - 1;
  const last = new Date(Date.UTC(year, m, 0)).getUTCDate();
  return `${year}-${pad(m)}-${pad(Math.min(d, last))}`;
}

/** Jour en cours à Paris (yyyy-MM-dd). */
export function todayInParis(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** 31/10/2026. */
export function formatDateFr(date: string | null | undefined): string {
  if (!date || !isIsoDate(date)) return '-';
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}`;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

/** Période de comparaison N-1 : mêmes dates, un an avant. */
export function previousRange(range: DateRange): DateRange {
  return { startDate: minusOneYear(range.startDate), endDate: minusOneYear(range.endDate) };
}

export interface PeriodPreset extends DateRange {
  id: string;
  label: string;
}

/**
 * Raccourcis de période : 30 derniers jours (défaut de la page), Auj., Hier, Mois, Année, puis une puce par année
 * révolue depuis le début du suivi, et All (depuis le début du suivi).
 */
export function periodPresets(today: string, since: string | null): PeriodPreset[] {
  const year = Number(today.slice(0, 4));
  const yesterday = addDays(today, -1);
  const out: PeriodPreset[] = [
    { id: '30d', label: '30 j', startDate: addDays(today, -29), endDate: today },
    { id: 'today', label: 'Auj.', startDate: today, endDate: today },
    { id: 'yesterday', label: 'Hier', startDate: yesterday, endDate: yesterday },
    { id: 'month', label: 'Mois', startDate: `${today.slice(0, 7)}-01`, endDate: today },
    { id: 'year', label: 'Année', startDate: `${year}-01-01`, endDate: today },
  ];
  const first = isIsoDate(since) ? Number(since.slice(0, 4)) : year;
  for (let y = Math.min(first, year); y < year; y++) {
    out.push({ id: `y${y}`, label: String(y), startDate: `${y}-01-01`, endDate: `${y}-12-31` });
  }
  out.push({ id: 'all', label: 'All', startDate: isIsoDate(since) && since <= today ? since : `${year}-01-01`, endDate: today });
  return out;
}

/** Raccourci qui correspond exactement à la période, ou null. */
export function activePreset(range: DateRange, presets: readonly PeriodPreset[]): string | null {
  return presets.find((p) => p.startDate === range.startDate && p.endDate === range.endDate)?.id ?? null;
}

// ==================== Critères à trois états (paramètre flags) ====================

/** « DOIT avoir » ou « NE DOIT PAS avoir » ; absent = neutre. */
export type CriterionState = 'must' | 'not';

/** Lecture de `flags` (« a,!b ») : clés connues seulement quand `known` est donné, sans doublon. */
export function parseFlags(raw: string | null | undefined, known?: readonly string[]): Map<string, CriterionState> {
  const out = new Map<string, CriterionState>();
  for (const part of (raw ?? '').split(',')) {
    const token = part.trim();
    if (!token) continue;
    const not = token.startsWith('!');
    const key = (not ? token.slice(1) : token).trim();
    if (!key || out.has(key)) continue;
    if (known && !known.includes(key)) continue;
    out.set(key, not ? 'not' : 'must');
  }
  return out;
}

/** Clic sur une puce : neutre, puis « DOIT avoir », puis « NE DOIT PAS avoir », puis neutre. */
export function nextCriterion(state: CriterionState | undefined): CriterionState | undefined {
  if (state === undefined) return 'must';
  if (state === 'must') return 'not';
  return undefined;
}

/** Paramètre `flags` dans l'ordre des puces (`order`), « ! » pour la négation ; vide si tout est neutre. */
export function serializeFlags(states: ReadonlyMap<string, CriterionState>, order: readonly string[]): string {
  const keys = [...order.filter((k) => states.has(k)), ...[...states.keys()].filter((k) => !order.includes(k))];
  return keys.map((k) => (states.get(k) === 'not' ? `!${k}` : k)).join(',');
}

/** `flags` après un clic sur la puce `key`. */
export function toggleFlag(raw: string | null | undefined, key: string, order: readonly string[]): string {
  const states = parseFlags(raw, order);
  const next = nextCriterion(states.get(key));
  if (next) states.set(key, next);
  else states.delete(key);
  return serializeFlags(states, order);
}

// ==================== Parcours d'accès : nœuds et échantillon ====================

/** Libellés des nœuds du parcours (identifiants de `focus`). */
export const FUNNEL_NODES: Record<string, string> = {
  registered: 'Inscription',
  tuto_arrived: 'Arrivé au tuto',
  no_tuto: 'Sans tuto',
  tuto_done: 'Tuto terminé',
  tuto_abandoned: 'Tuto abandonné',
  tuto_lost_during: 'Perdu pendant le tuto',
  tuto_lost_after: 'Perdu après le tuto',
  game_login: 'Login serveur de jeu',
  lost_before_game: 'Perdu : jamais sur un serveur de jeu',
  played_5: 'Joué 5 min',
  under_5: 'Perdu : moins de 5 min de jeu',
  played_1h: 'Joué 1 h et plus',
  under_1h: 'Entre 5 min et 1 h',
};

/** Lecture de `focus` : nœuds connus, sans doublon, sans la racine (tout le monde). */
export function parseFocus(raw: string | null | undefined): string[] {
  const out: string[] = [];
  for (const part of (raw ?? '').split(',')) {
    const id = part.trim();
    if (id && id !== 'registered' && FUNNEL_NODES[id] && !out.includes(id)) out.push(id);
  }
  return out;
}

/** Échantillon après un clic sur un nœud : ses identifiants ajoutés à la fin (cumulatif), la racine ne change rien. */
export function addFocus(focus: readonly string[], ids: readonly string[]): string[] {
  const out = [...focus];
  for (const id of ids) {
    if (id !== 'registered' && FUNNEL_NODES[id] && !out.includes(id)) out.push(id);
  }
  return out;
}

/** Fil d'Ariane : garder l'échantillon jusqu'à l'étape `index` incluse (-1 = tout le monde). */
export function focusUntil(focus: readonly string[], index: number): string[] {
  return index < 0 ? [] : focus.slice(0, index + 1);
}

// ==================== Formats ====================

const nf1 = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nf0 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 12 345 (espace fine), « - » sans valeur. */
export function formatCount(n: number | null | undefined): string {
  return n == null || !Number.isFinite(n) ? '-' : nf0.format(n);
}

/** 64,2 %, « - » sans valeur. */
export function formatPct(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '-' : `${nf1.format(v)} %`;
}

/** 12,50 €, « - » sans valeur. */
export function formatEuros(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '-' : `${nf2.format(v)} €`;
}

/** Temps de jeu : « 3h 29min », « 45min », « 0min ». */
export function formatPlaytime(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '-';
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
}

/** Délai : « < 1 min », « 45 min », « 5h 12min », « 3 j 4h » ; « - » sans valeur. */
export function formatDelay(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '-';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return '< 1 min';
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  }
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return h > 0 ? `${d} j ${h}h` : `${d} j`;
}

/** Variation face à N-1 : « +12,5 % », « -3,1 % », « 0,0 % », ou « pas de données ». */
export function formatChange(change: number | null | undefined): string {
  if (change == null || !Number.isFinite(change)) return 'pas de données';
  if (change > 0) return `+${nf1.format(change)} %`;
  if (change < 0) return `-${nf1.format(Math.abs(change))} %`;
  return `${nf1.format(0)} %`;
}

/** Couleur de la variation : une hausse est bonne, sauf pour le double compte (`higherIsWorse`). */
export function changeTone(change: number | null | undefined, higherIsWorse = false): 'good' | 'bad' | 'neutral' | 'none' {
  if (change == null || !Number.isFinite(change)) return 'none';
  if (change === 0) return 'neutral';
  const up = change > 0;
  return up !== higherIsWorse ? 'good' : 'bad';
}

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

export type RegistrationPeriod = 'day' | 'week' | 'month';

/** Graduation d'un seau : « 31/10 » (jour, semaine), « oct. 26 » (mois). */
export function bucketTick(date: string, period: RegistrationPeriod): string {
  if (!isIsoDate(date)) return date;
  const [y, m, d] = date.split('-');
  return period === 'month' ? `${MONTHS_SHORT[Number(m) - 1]} ${y.slice(2)}` : `${d}/${m}`;
}

/** Titre d'un seau : « 31/10/2026 », « Semaine du 27/10/2026 », « octobre 2026 ». */
export function bucketTitle(date: string, period: RegistrationPeriod): string {
  if (!isIsoDate(date)) return date;
  if (period === 'month') return `${MONTHS[Number(date.slice(5, 7)) - 1]} ${date.slice(0, 4)}`;
  return period === 'week' ? `Semaine du ${formatDateFr(date)}` : formatDateFr(date);
}

// ==================== Satellite écrivain (NationsGlory) ====================

/** Ce qu'il faut d'un groupe de project-groups (forme de ProjectGroupDTO). */
export interface WriterGroupLike {
  key: string;
  label: string;
  available: boolean;
  reason?: string;
  retired?: boolean;
  capabilities?: string[];
}

export type WriterTarget =
  | { kind: 'ready'; gameGroup: string | null; label: string | null }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'none' }
  | { kind: 'notMember'; label: string }
  | { kind: 'unavailable'; label: string; reason: string };

/**
 * Satellite à interroger. Projet sans groupes (Paladium) : appel normal. Projet à groupes : le groupe qui annonce la
 * capacité « onboarding », forcé pour toutes les requêtes de la page ; refusé si le membre du staff n'y est pas affecté.
 *
 * @param allowed groupes de l'affectation du membre du staff (session), null = tous
 */
export function resolveWriter(
  grouped: boolean,
  entry: { groups: readonly WriterGroupLike[] } | undefined,
  status: 'idle' | 'loading' | 'loaded' | 'error',
  allowed: readonly string[] | null,
  error?: string | null,
): WriterTarget {
  if (!grouped) return { kind: 'ready', gameGroup: null, label: null };
  if (!entry) {
    if (status === 'error') return { kind: 'error', message: error || 'groupes de jeu illisibles' };
    if (status === 'loaded') return { kind: 'ready', gameGroup: null, label: null };
    return { kind: 'loading' };
  }
  const writer = entry.groups.find((g) => !g.retired && (g.capabilities ?? []).includes(ONBOARDING_CAPABILITY));
  if (!writer) return { kind: 'none' };
  if (writer.reason === 'not-member' || (allowed && !allowed.includes(writer.key))) return { kind: 'notMember', label: writer.label };
  if (!writer.available) return { kind: 'unavailable', label: writer.label, reason: writer.reason || 'indisponible' };
  return { kind: 'ready', gameGroup: writer.key, label: writer.label };
}

// ==================== Tableaux par groupe ====================

/** Ligne visible : un groupe de l'affectation, « (sans groupe) » et l'ensemble toujours. */
function visibleRow(group: string, allowed: readonly string[] | null): boolean {
  return group === 'none' || group === 'all' || !allowed || allowed.includes(group);
}

/** Valeur de tri d'abord, null en dernier, puis l'ordre d'arrivée. */
function sortNullLast<T>(rows: readonly T[], value: (r: T) => number | null, dir: 1 | -1): T[] {
  return rows
    .map((r, i) => ({ r, i, v: value(r) }))
    .sort((a, b) => {
      if (a.v == null && b.v == null) return a.i - b.i;
      if (a.v == null) return 1;
      if (b.v == null) return -1;
      return a.v === b.v ? a.i - b.i : (a.v - b.v) * dir;
    })
    .map((x) => x.r);
}

/**
 * Lignes du tableau Intégration. Projet sans groupes : la seule ligne du projet. Projet à groupes : « Ensemble »
 * épinglée, puis les lignes visibles par abandon croissant.
 */
export function accessRows(
  resp: { data: OnboardingAccessRow[]; total: OnboardingAccessRow },
  hasGroups: boolean,
  allowed: readonly string[] | null,
): { pinned: OnboardingAccessRow | null; rows: OnboardingAccessRow[] } {
  if (!hasGroups) return { pinned: null, rows: resp.data.slice(0, 1) };
  const rows = resp.data.filter((r) => visibleRow(r.group, allowed));
  return { pinned: resp.total, rows: sortNullLast(rows, (r) => r.abandon2d, 1) };
}

/** Lignes du tableau Conversion : comme Intégration, triées par % de 1er achat décroissant. */
export function conversionRows(
  resp: { data: OnboardingConversionRow[]; total: OnboardingConversionRow },
  hasGroups: boolean,
  allowed: readonly string[] | null,
): { pinned: OnboardingConversionRow | null; rows: OnboardingConversionRow[] } {
  if (!hasGroups) return { pinned: null, rows: resp.data.slice(0, 1) };
  const rows = resp.data.filter((r) => visibleRow(r.group, allowed));
  return { pinned: resp.total, rows: sortNullLast(rows, (r) => r.store?.pct ?? null, -1) };
}

/** Groupes proposés par le filtre « Groupe » : ceux de meta dans l'affectation du membre du staff. */
export function groupChoices(meta: Pick<OnboardingMetaResponse, 'groups'>, allowed: readonly string[] | null) {
  return meta.groups.filter((g) => !allowed || allowed.includes(g.key));
}

// ==================== Sankey ====================

export type SankeyKind = 'main' | 'tuto' | 'lost' | 'stop';

export interface SankeyNodeIn {
  id: string;
  label: string;
  value: number;
  column: number;
  kind: SankeyKind;
  /** Identifiants ajoutés à l'échantillon au clic ; absent = nœud non cliquable. */
  focus?: string[];
}

export interface SankeyLinkIn {
  source: string;
  target: string;
  value: number;
}

export interface SankeyGraph {
  nodes: SankeyNodeIn[];
  links: SankeyLinkIn[];
  /** Base des pourcentages (inscrits ou arrivés au tuto). */
  base: number;
}

export interface SankeyNodeOut extends SankeyNodeIn {
  x: number;
  y: number;
  h: number;
}

export interface SankeyLinkOut extends SankeyLinkIn {
  path: string;
  width: number;
  kind: SankeyKind;
}

export interface SankeyLayout {
  nodes: SankeyNodeOut[];
  links: SankeyLinkOut[];
}

const nonNegative = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);

/** Parcours d'accès : nœuds et bandes du contrat (branche du tuto seulement si le projet en a un). */
export function funnelGraph(tree: OnboardingFunnelResponse): SankeyGraph {
  const n = (id: string, value: number, column: number, kind: SankeyKind, focus?: string[]): SankeyNodeIn => ({
    id, label: FUNNEL_NODES[id], value: nonNegative(value), column, kind, focus: focus ?? (id === 'registered' ? undefined : [id]),
  });
  const l = (source: string, target: string, value: number): SankeyLinkIn => ({ source, target, value: nonNegative(value) });
  const t = tree.tuto;
  if (!t) {
    return {
      base: tree.registrations,
      nodes: [
        n('registered', tree.registrations, 0, 'main'),
        n('game_login', tree.gameLogin, 1, 'main'),
        n('lost_before_game', tree.lostBeforeGame, 1, 'lost'),
        n('played_5', tree.played5, 2, 'main'),
        n('under_5', tree.under5, 2, 'lost'),
        n('played_1h', tree.played1h, 3, 'main'),
        n('under_1h', tree.under1h, 3, 'stop'),
      ],
      links: [
        l('registered', 'game_login', tree.gameLogin),
        l('registered', 'lost_before_game', tree.lostBeforeGame),
        l('game_login', 'played_5', tree.played5),
        l('game_login', 'under_5', tree.under5),
        l('played_5', 'played_1h', tree.played1h),
        l('played_5', 'under_1h', tree.under1h),
      ],
    };
  }
  return {
    base: tree.registrations,
    nodes: [
      n('registered', tree.registrations, 0, 'main'),
      n('tuto_arrived', t.arrived, 1, 'tuto'),
      n('tuto_done', t.done, 2, 'tuto'),
      n('tuto_abandoned', t.abandoned, 2, 'tuto'),
      n('tuto_lost_during', t.lostDuring, 2, 'lost'),
      n('no_tuto', t.noTuto, 2, 'main'),
      n('game_login', tree.gameLogin, 3, 'main'),
      n('tuto_lost_after', t.lostAfter, 3, 'lost'),
      // Ici seulement ceux qui n'ont jamais vu le tuto : les pertes du tuto ont leurs nœuds.
      n('lost_before_game', t.noTutoLost, 3, 'lost', ['no_tuto', 'lost_before_game']),
      n('played_5', tree.played5, 4, 'main'),
      n('under_5', tree.under5, 4, 'lost'),
      n('played_1h', tree.played1h, 5, 'main'),
      n('under_1h', tree.under1h, 5, 'stop'),
    ],
    links: [
      l('registered', 'tuto_arrived', t.arrived),
      l('registered', 'no_tuto', t.noTuto),
      l('tuto_arrived', 'tuto_done', t.done),
      l('tuto_arrived', 'tuto_abandoned', t.abandoned),
      l('tuto_arrived', 'tuto_lost_during', t.lostDuring),
      l('tuto_done', 'game_login', t.done - t.lostAfter),
      l('tuto_done', 'tuto_lost_after', t.lostAfter),
      l('tuto_abandoned', 'game_login', t.abandoned),
      l('no_tuto', 'game_login', t.noTutoGame),
      l('no_tuto', 'lost_before_game', t.noTutoLost),
      l('game_login', 'played_5', tree.played5),
      l('game_login', 'under_5', tree.under5),
      l('played_5', 'played_1h', tree.played1h),
      l('played_5', 'under_1h', tree.under1h),
    ],
  };
}

/**
 * Le flux compte-t-il les étapes vues (satellite 1.0.36 : clés `k1`, `k2`..., « 2 étapes vues ») plutôt qu'un ordre
 * d'étapes (1.0.35) ? Les étapes d'un tuto en visite libre n'ont pas d'ordre : seul le nombre vu a un sens.
 */
export function isCountFlow(flow: NonNullable<OnboardingTutorialResponse['flow']>): boolean {
  return flow.steps.length > 0 && flow.steps.every((s) => /^k\d+$/.test(s.key));
}

/**
 * Tutoriel d'accueil : arrivés au tuto (100 %), puis à chaque palier ceux qui continuent et ceux qui s'arrêtent là,
 * plus « Arrêt avant la 1re étape ». Satellite 1.0.36 : paliers = nombre d'étapes vues, quel que soit l'ordre
 * (« Arrêt à 2 étapes vues ») ; avant : étapes de la config (« Arrêt après « Machines » »). Une bande ne dépasse
 * jamais ni ceux qui continuent ni ceux qui atteignent le palier suivant.
 */
export function tutorialGraph(flow: NonNullable<OnboardingTutorialResponse['flow']>): SankeyGraph {
  const steps = flow.steps;
  const counting = isCountFlow(flow);
  const stopLabel = (label: string) => (counting ? `Arrêt à ${label}` : `Arrêt après « ${label} »`);
  const nodes: SankeyNodeIn[] = [
    { id: 'arrived', label: 'Arrivé au tuto', value: nonNegative(flow.entered), column: 0, kind: 'tuto' },
    { id: 'before_first', label: 'Arrêt avant la 1re étape', value: nonNegative(flow.stoppedBeforeFirst), column: 1, kind: 'stop' },
  ];
  const links: SankeyLinkIn[] = [];
  steps.forEach((s, i) => {
    nodes.push({ id: `step:${s.key}`, label: s.label, value: nonNegative(s.reached), column: i + 1, kind: 'tuto' });
    nodes.push({ id: `stop:${s.key}`, label: stopLabel(s.label), value: nonNegative(s.stopped), column: i + 2, kind: 'stop' });
  });
  if (steps.length > 0) {
    const first = steps[0];
    links.push({ source: 'arrived', target: `step:${first.key}`, value: Math.min(nonNegative(flow.entered - flow.stoppedBeforeFirst), nonNegative(first.reached)) });
  }
  links.push({ source: 'arrived', target: 'before_first', value: nonNegative(flow.stoppedBeforeFirst) });
  steps.forEach((s, i) => {
    const next = steps[i + 1];
    if (next) {
      links.push({ source: `step:${s.key}`, target: `step:${next.key}`, value: Math.min(nonNegative(s.reached - s.stopped), nonNegative(next.reached)) });
    }
    links.push({ source: `step:${s.key}`, target: `stop:${s.key}`, value: nonNegative(s.stopped) });
  });
  // L'étape passe avant l'arrêt dans chaque colonne (l'arrêt de l'étape précédente reste en bas).
  const ordered = [...nodes].sort((a, b) => a.column - b.column || rankInColumn(a) - rankInColumn(b));
  return { nodes: ordered, links, base: nonNegative(flow.entered) };
}

function rankInColumn(n: SankeyNodeIn): number {
  return n.kind === 'stop' ? 1 : 0;
}

export interface SankeyOptions {
  width: number;
  height: number;
  nodeWidth: number;
  /** Écart vertical entre deux nœuds d'une colonne. */
  gap: number;
  /** Hauteur ajoutée à chaque nœud, pour qu'un nœud à 0 reste visible et cliquable. */
  minNodeHeight: number;
}

/**
 * Mise en page du sankey : une colonne par profondeur, hauteur des nœuds et largeur des bandes proportionnelles au
 * nombre de joueurs (même échelle partout), colonnes centrées, bandes empilées dans l'ordre vertical de l'autre bout.
 */
export function layoutSankey(graph: SankeyGraph, opts: SankeyOptions): SankeyLayout {
  const columns = Math.max(1, ...graph.nodes.map((n) => n.column + 1));
  const byColumn: SankeyNodeIn[][] = Array.from({ length: columns }, () => []);
  for (const node of graph.nodes) byColumn[node.column].push(node);

  let k = Number.POSITIVE_INFINITY;
  for (const col of byColumn) {
    if (col.length === 0) continue;
    const sum = col.reduce((s, n) => s + n.value, 0);
    const room = opts.height - opts.gap * (col.length - 1) - opts.minNodeHeight * col.length;
    if (sum > 0) k = Math.min(k, Math.max(0, room) / sum);
  }
  if (!Number.isFinite(k)) k = 0;

  const step = columns > 1 ? (opts.width - opts.nodeWidth) / (columns - 1) : 0;
  const placed = new Map<string, SankeyNodeOut>();
  for (const col of byColumn) {
    const heights = col.map((n) => n.value * k + opts.minNodeHeight);
    const total = heights.reduce((s, h) => s + h, 0) + opts.gap * Math.max(0, col.length - 1);
    let y = Math.max(0, (opts.height - total) / 2);
    col.forEach((n, i) => {
      placed.set(n.id, { ...n, x: n.column * step, y, h: heights[i] });
      y += heights[i] + opts.gap;
    });
  }

  const kindOf = (id: string): SankeyKind => placed.get(id)?.kind ?? 'main';
  const visible = graph.links.filter((l) => l.value > 0 && placed.has(l.source) && placed.has(l.target));
  const outOffset = new Map<string, number>();
  const inOffset = new Map<string, number>();
  const centerY = (id: string) => {
    const n = placed.get(id)!;
    return n.y + n.h / 2;
  };
  // Bandes sortantes triées par la hauteur de leur cible, entrantes par celle de leur source : elles se croisent le moins possible.
  const outOrder = [...visible].sort((a, b) => centerY(a.target) - centerY(b.target));
  const inOrder = [...visible].sort((a, b) => centerY(a.source) - centerY(b.source));
  const sourceY = new Map<SankeyLinkIn, number>();
  const targetY = new Map<SankeyLinkIn, number>();
  for (const link of outOrder) {
    const node = placed.get(link.source)!;
    const used = outOffset.get(link.source) ?? 0;
    const w = link.value * k;
    sourceY.set(link, node.y + opts.minNodeHeight / 2 + used + w / 2);
    outOffset.set(link.source, used + w);
  }
  for (const link of inOrder) {
    const node = placed.get(link.target)!;
    const used = inOffset.get(link.target) ?? 0;
    const w = link.value * k;
    targetY.set(link, node.y + opts.minNodeHeight / 2 + used + w / 2);
    inOffset.set(link.target, used + w);
  }

  const links: SankeyLinkOut[] = visible.map((link) => {
    const s = placed.get(link.source)!;
    const t = placed.get(link.target)!;
    const x0 = s.x + opts.nodeWidth;
    const x1 = t.x;
    const y0 = sourceY.get(link)!;
    const y1 = targetY.get(link)!;
    const xm = (x0 + x1) / 2;
    const r = (v: number) => Math.round(v * 10) / 10;
    return {
      ...link,
      width: Math.max(1, link.value * k),
      kind: kindOf(link.target),
      path: `M${r(x0)},${r(y0)} C${r(xm)},${r(y0)} ${r(xm)},${r(y1)} ${r(x1)},${r(y1)}`,
    };
  });
  return { nodes: [...placed.values()], links };
}

/** Part d'un nœud dans la base (inscrits ou arrivés), arrondie à 0,1 ; null sans base. */
export function shareOf(value: number, base: number): number | null {
  return base > 0 ? Math.round((value / base) * 1000) / 10 : null;
}

// ==================== Textes ====================

/** Explications au survol, une par section (définitions adaptées au Bedrock). */
export const EXPLAIN = {
  cards:
    "Inscription = première connexion au réseau (première fois vu par le proxy), lobby compris. Login serveur de jeu = passé au moins une fois hors lobby ; pour les joueurs repris de l'historique de Paladium, il est estimé par le premier jour avec de l'XP de métier ou du minage (un joueur venu en jeu sans l'un ni l'autre compte comme jamais venu). Temps de jeu = temps passé hors lobby, cumulé depuis l'inscription ; pour les joueurs repris de l'historique de Paladium, le temps d'avant le suivi détaillé compte aussi le lobby. Double compte = un autre compte inscrit avant lui depuis la même adresse IP (gardée seulement sous forme d'empreinte) : les IP partagées (même foyer, opérateur mobile) gonflent ce chiffre. Chaque carte est comparée à la même période un an avant.",
  registrations:
    "Nombre de joueurs dont la première connexion au réseau tombe dans chaque jour, semaine (du lundi) ou mois. « Comparer » superpose la même période un an avant.",
  funnel:
    "Largeur des bandes = nombre de joueurs · % = part des inscrits de la période · clic sur un nœud = ne garder que ces joueurs (les clics se cumulent, le fil d'Ariane permet de revenir). « Perdu » est une photo du moment : un inscrit récent peut encore avancer. Joué 5 min et Joué 1 h comptent le temps hors lobby.",
  tutorial:
    "Arrivée = premier point vu sur l'île tuto. Une étape (un quartier de l'île) est atteinte quand au moins un de ses points est vu ; les quartiers se visitent dans n'importe quel ordre. Le diagramme compte combien d'étapes chaque arrivé a vues (1, 2, ... toutes), quel que soit l'ordre ; « Arrêt à 2 étapes vues » = joueurs qui en ont vu deux et pas plus. Le tableau donne, pour chaque étape, les joueurs qui l'ont vue et leur part des arrivés. Terminé = carnet complet. Suit l'échantillon du parcours d'accès.",
  access:
    "Joueurs = inscrits de la période dont le groupe principal (celui où ils ont le plus de temps de jeu) est ce groupe ; « (sans groupe) » = jamais sur un serveur d'un groupe. Sous chaque pourcentage : délai médian entre l'inscription et la première fois, sur ceux qui l'ont fait (les membres d'avant le suivi comptent comme faits, sans délai). Abandon < 2 j : parmi les inscrits depuis au moins 7 jours ayant joué au moins 1 h, part dont la dernière connexion est restée dans les 2 jours suivant l'inscription.",
  conversion:
    "1er achat boutique et 1er grade : part des joueurs et délai médian depuis l'inscription. Colonnes des grades : part exclusive, chaque joueur ne compte que dans son grade le plus haut. Dépenses par joueur : les non-acheteurs comptent pour 0 € ; par client : acheteurs seulement. « Ensemble » recalcule les taux sur tous les joueurs.",
  distribution:
    "Nombre d'inscrits de la période par tranche de temps de jeu cumulé (hors lobby). « Comparer » superpose la même période un an avant.",
} as const;

/**
 * Ce que le suivi détaillé ajoute pour CE projet : lobby et serveur de jeu toujours, puis factions ou pays (critère
 * d'entrée du projet), grades (grades boutique déclarés) et tutoriel (étapes déclarées). NationsGlory : pas de tuto.
 */
export function detailedItems(meta: Partial<Pick<OnboardingMetaResponse, 'integration' | 'ranks' | 'tutorialSteps'>>): string[] {
  const out = ['lobby', 'serveur de jeu'];
  const keys = (meta.integration ?? []).map((c) => c.key);
  if (keys.includes('join_faction')) out.push('factions');
  if (keys.includes('join_country')) out.push('pays');
  if ((meta.ranks ?? []).length > 0) out.push('grades');
  if ((meta.tutorialSteps ?? []).length > 0) out.push('tutoriel');
  return out;
}

/** « a, b et c ». */
function joinFr(items: readonly string[]): string {
  return items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`;
}

/** Lignes de la note sous le titre (début du suivi). */
export function trackingNotes(
  meta: Pick<OnboardingMetaResponse, 'trackedSince' | 'detailedSince'> & Partial<Pick<OnboardingMetaResponse, 'integration' | 'ranks' | 'tutorialSteps'>>,
): string[] {
  const out: string[] = [];
  if (meta.trackedSince) {
    out.push(`Données suivies depuis le ${formatDateFr(meta.trackedSince)} : les périodes antérieures sont incomplètes.`);
  }
  if (meta.detailedSince && meta.detailedSince !== meta.trackedSince) {
    const what = joinFr(detailedItems(meta));
    out.push(`${what.charAt(0).toUpperCase()}${what.slice(1)} suivis depuis le ${formatDateFr(meta.detailedSince)}.`);
  }
  return out;
}
